import { useState } from "react";
import {
  Text,
  Icon,
  Button,
  TwAccordion as Accordion,
} from "@virkesborsen/ui-component-library";
import { useDrone } from "../../../context/DroneContext";

const comparePackages = [
  {
    title: "Ortofoto",
    description: "Högupplösta bilder av din skog ger dig överblick och insikt",
    includedIn: ["bas", "skadeanalys-bas", "skadeanalys-plus", "fmp"],
    key: "bas",
  },
  {
    title: "Skadeanalys Bas",
    description: "Högupplösta bilder och analys för barkborreskador",
    includedIn: ["skadeanalys-bas", "skadeanalys-plus", "fmp"],
    key: "skadeanalys-bas",
  },
  {
    title: "Skadeanalys Plus",
    description: "Högupplösta bilder och analys för vindfällen samt barkborreskador",
    includedIn: ["skadeanalys-plus", "fmp"],
    key: "skadeanalys-plus",
  },
  {
    title: "Skogsbruksplan",
    description: "En helt ny skogsbruksplan baserad på drönarbilderna",
    includedIn: ["fmp"],
    key: "fmp",
  },
];

export default function DroneComparison() {
  const { openCheckout } = useDrone();
  const [selectedPackage, setSelectedPackage] = useState("plus");

  // Map button selection to column key in table
  const selectedColumnKey =
    selectedPackage === "bas"
      ? "skadeanalys-bas"
      : selectedPackage === "plus"
      ? "skadeanalys-plus"
      : "fmp";

  const columnLabels = {
    bas: "Grundpaket",
    "skadeanalys-bas": "Skadeanalys Bas",
    "skadeanalys-plus": "Skadeanalys Plus",
    fmp: "Skogsbruksplan",
  };

  return (
    <div className="h-full w-full bg-sandstone-dark">
      <div className="max-w-[1320px] mx-auto flex flex-col">
        <section className="flex flex-col gap-8 m-4 lg:m-16">
          <div className="flex flex-col gap-4">
            <Text variant="subsectionHeaderMedium">
              Vad ingår i analyspaketen?
            </Text>
            <Text variant="introLeadSmallRegular">
              Alla paket börjar med Ortofoto, för att utöka analysen kan ett av
              tilläggen väljas till.
            </Text>
          </div>
          <div className="flex flex-col w-full">
            <div className="flex flex-row gap-1 pb-8 lg:hidden">
              <Button
                tw
                variant={selectedPackage === "bas" ? "quaternary" : "secondary"}
                fullWidth
                onClick={() => setSelectedPackage("bas")}
              >
                Bas
              </Button>
              <Button
                tw
                variant={
                  selectedPackage === "plus" ? "quaternary" : "secondary"
                }
                fullWidth
                onClick={() => setSelectedPackage("plus")}
              >
                Plus
              </Button>
              <Button
                tw
                variant={selectedPackage === "fmp" ? "quaternary" : "secondary"}
                fullWidth
                onClick={() => setSelectedPackage("fmp")}
              >
                Skogsbruksplan
              </Button>
            </div>
            <table className="table-fixed w-full lg:table-auto">
              {/* Desktop header - show all columns */}
              <thead className="hidden lg:table-header-group">
                <tr>
                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-left py-2"
                  >
                    Det här får du
                  </Text>

                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-center py-4"
                  >
                    Grundpaket
                  </Text>

                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-center py-4 bg-purple-plum-50 rounded-t-xl lg:rounded-tl-lg lg:rounded-tr-none"
                  >
                    Skadeanalys Bas
                  </Text>

                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-center py-4 bg-purple-plum-50 rounded-t-xl lg:rounded-none"
                  >
                    Skadeanalys Plus
                  </Text>

                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-center py-4 bg-purple-plum-50 rounded-t-xl lg:rounded-tr-lg lg:rounded-tl-none"
                  >
                    Skogsbruksplan
                  </Text>
                </tr>
              </thead>
              {/* Mobile header - show only Bas and selected */}
              <thead className="table-header-group lg:hidden">
                <tr>
                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-left py-2 px-2 w-[40%] text-xs"
                  >
                    Det här får du
                  </Text>
                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-center py-2 px-1 w-[30%] text-xs"
                  >
                    {columnLabels["bas"]}
                  </Text>
                  <Text
                    variant="labelLargeMedium"
                    as="th"
                    className="text-center py-2 px-1 w-[30%] text-[12px] bg-purple-plum-50 rounded-t-xl"
                  >
                    {columnLabels[selectedColumnKey]}
                  </Text>
                </tr>
              </thead>

              <tbody>
                {comparePackages.map((comparePackage) => (
                  <tr className="border-solid border-t-[0.5px] border-charcoal-500/16">
                    <td className="py-3 pr-2 w-[50%] lg:w-auto lg:py-4">
                      <Accordion>
                        <Accordion.Header>
                          <Text
                            variant="labelLargeRegular"
                            className="text-charcoal-500 text-[12px] lg:text-base"
                          >
                            {comparePackage.title}
                          </Text>
                        </Accordion.Header>
                        <Accordion.Content>
                          <Text
                            variant="overlineLargeRegular"
                            className="text-pebble-400 text-[10px] lg:text-xs"
                          >
                            {comparePackage.description}
                          </Text>
                        </Accordion.Content>
                      </Accordion>
                    </td>
                    {/* Desktop cells - all columns */}
                    {comparePackages.map((p) => (
                      <td
                        className={`hidden lg:table-cell ${
                          p.key !== "bas" ? "bg-purple-plum-50" : ""
                        }`}
                      >
                        {comparePackage.includedIn.includes(p.key) ? (
                          <Icon
                            tw
                            size="large"
                            name="CheckSimple"
                            className="fill-purple-plum-500"
                          />
                        ) : (
                          <Icon
                            tw
                            size="large"
                            name="CrossSimple"
                            className="fill-charcoal-500"
                          />
                        )}
                      </td>
                    ))}

                    {/* Mobile cells - only Bas and selected */}
                    {["bas", selectedColumnKey].map((key) => (
                      <td
                        className={`${
                          key !== "bas" ? "bg-purple-plum-50" : ""
                        } lg:hidden table-cell text-center py-3 px-1 w-[25%]`}
                      >
                        {comparePackage.includedIn.includes(key) ? (
                          <Icon
                            tw
                            size="medium"
                            name="CheckSimple"
                            className="fill-purple-plum-500"
                          />
                        ) : (
                          <Icon
                            tw
                            size="medium"
                            name="CrossSimple"
                            className="fill-charcoal-500"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-solid border-t-[0.5px] border-charcoal-500/16">
                  <Text
                    variant="labelLargeRegular"
                    className="text-charcoal-500 py-3 lg:py-4 text-left w-[50%] lg:w-auto"
                    as="td"
                  >
                    Pris
                  </Text>

                  {/* Desktop prices - all columns */}
                  <Text
                    variant="labelLargeRegular"
                    className="hidden lg:table-cell text-charcoal-500 py-4 px-2 text-center"
                    as="td"
                  >
                    Från 119 kr/hektar
                  </Text>
                  <Text
                    variant="labelLargeRegular"
                    className="hidden lg:table-cell text-charcoal-500 py-4 px-2 text-center bg-purple-plum-50 rounded-b-xl lg:rounded-bl-lg lg:rounded-br-none"
                    as="td"
                  >
                    Från 25 kr/hektar
                  </Text>
                  <Text
                    variant="labelLargeRegular"
                    className="hidden lg:table-cell text-charcoal-500 py-4 px-2 text-center bg-purple-plum-50 rounded-b-xl lg:rounded-b-none"
                    as="td"
                  >
                    Från 50 kr/hektar
                  </Text>
                  <Text
                    variant="labelLargeRegular"
                    className="hidden lg:table-cell text-charcoal-500 py-4 px-2 text-center bg-purple-plum-50 rounded-b-xl lg:rounded-br-lg lg:rounded-bl-none"
                    as="td"
                  >
                    Från 99 kr/hektar
                  </Text>

                  {/* Mobile prices - only Bas and selected */}
                  <Text
                    variant="labelLargeRegular"
                    className="lg:hidden text-charcoal-500 py-3 px-1 text-center w-[25%] text-xs"
                    as="td"
                  >
                    Från 119 kr/hektar
                  </Text>
                  <Text
                    variant="labelLargeRegular"
                    className="lg:hidden text-charcoal-500 py-3 px-1 text-center bg-purple-plum-50 rounded-b-xl w-[25%] text-xs"
                    as="td"
                  >
                    {selectedColumnKey === "skadeanalys-bas" &&
                      "Från 25 kr/hektar"}
                    {selectedColumnKey === "skadeanalys-plus" &&
                      "Från 50 kr/hektar"}
                    {selectedColumnKey === "fmp" && "Från 99 kr/hektar"}
                  </Text>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <section className="p-8 bg-charcoal-900 rounded-xl m-4 lg:m-16">
          <Text variant="subheaderSmallMedium" className="text-white-50 pb-8">
            Prisförslag
          </Text>

          <div className="flex flex-col gap-4 pb-6">
            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2 items-center pl-1-5">
                <Icon
                  tw
                  name="DotSimple"
                  size="xsmall"
                  className="fill-white-50"
                />
                <Text variant="bodyMediumBaseMedium" className="text-white-50">
                  Grundpaket:
                </Text>
              </div>
              <ul className="flex flex-col gap-2 list-disc pl-5 marker:text-white-50 marker:text-2xs">
                <li>
                  <div className="flex flex-row justify-between items-center w-full">
                    <Text
                      variant="bodyMediumBaseMedium"
                      className="text-white-50"
                    >
                      Ortofoto
                    </Text>
                    <Text
                      variant="bodySmallRegular"
                      className="text-spring-yellow-500"
                    >
                      119 kr/ha
                    </Text>
                  </div>
                </li>
              </ul>
            </div>

            <div className="flex flex-col gap-2">
              <div className="flex flex-row gap-2 items-center">
                <Icon
                  tw
                  name="PlusSimple"
                  size="medium"
                  className="fill-white-50"
                />
                <Text variant="bodyMediumBaseMedium" className="text-white-50">
                  Valbara tillägg:
                </Text>
              </div>
              <ul className="flex flex-col gap-2 list-disc pl-5 marker:text-white-50 marker:text-2xs">
                <li>
                  <div className="flex flex-row justify-between items-center w-full">
                    <Text variant="bodySmallRegular" className="text-white-600">
                      Skadeanalys Bas
                    </Text>
                    <Text
                      variant="bodySmallMedium"
                      className="text-spring-yellow-500"
                    >
                      +25 kr/ha
                    </Text>
                  </div>
                </li>
                <li>
                  <div className="flex flex-row justify-between items-center w-full">
                    <Text variant="bodySmallRegular" className="text-white-600">
                      Skadeanalys Plus
                    </Text>
                    <Text
                      variant="bodySmallMedium"
                      className="text-spring-yellow-500"
                    >
                      +50 kr/ha
                    </Text>
                  </div>
                </li>
                <li>
                  <div className="flex flex-row justify-between items-center w-full">
                    <Text variant="bodySmallRegular" className="text-white-600">
                      Skogsbruksplan
                    </Text>
                    <Text
                      variant="bodySmallMedium"
                      className="text-spring-yellow-500"
                    >
                      +99 kr/ha
                    </Text>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col gap-4 pb-4 lg:flex-row">
            <Button tw variant="secondary" onClick={openCheckout}>
              <Icon
                tw
                name="BankIdSimple"
                size="large"
                className="fill-charcoal-500"
              />
              <Text variant="bodySmallMedium" className="text-charcoal-500">
                Beställ drönarflygning
              </Text>
            </Button>
            <Button
              tw
              variant="outline"
              className="hover:bg-charcoal-400"
              onClick={() => {
                window.location.href = "/kontakta-oss/";
              }}
            >
              <Text variant="bodySmallMedium" className="text-white-50">
                Kontakta rådgivare
              </Text>
            </Button>
          </div>

          <div className="text-left lg:text-right">
            <Text variant="bodyExtraSmallRegular" className="text-white-600">
              *En rådgivare kontaktar dig vid behov.
            </Text>
          </div>
        </section>
      </div>
    </div>
  );
}
